import { Box, Typography, useTheme } from '@mui/material';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useMemo } from 'react';
import { ExternalGuestAttendeeStatsItem } from '../../../../types/dashboard';
import { CustomChip } from '../../../../../layout/styledcomponents/CustomChip';
import GenericTable from '../../../../shared/GenericTable';
import {
    getAttendeeStatusChipTone,
    getReportChipSx,
} from '../../reportChartConfig';
import { dashboardItemsTableDefaults } from '../../dashboardReportTableConfig';

type ExternalGuestAttendeesItemsListProps = {
    attendees: ExternalGuestAttendeeStatsItem[];
};

const capitalize = (value?: string) =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : '';

export default function ExternalGuestAttendeesItemsList({
    attendees,
}: ExternalGuestAttendeesItemsListProps) {
    const theme = useTheme();

    const columns: MRT_ColumnDef<ExternalGuestAttendeeStatsItem>[] = useMemo(
        () => [
            {
                accessorKey: 'attendeeName',
                header: 'Attendee',
                size: 160,
            },
            {
                accessorKey: 'email',
                header: 'Email',
                size: 180,
            },
            {
                accessorKey: 'status',
                header: 'Status',
                size: 120,
                Cell: ({ cell }) => (
                    <CustomChip
                        label={capitalize(cell.getValue<string>())}
                        sx={getReportChipSx(
                            getAttendeeStatusChipTone(cell.getValue<string>()),
                            theme,
                        )}
                        variant='outlined'
                    />
                ),
            },
            {
                accessorKey: 'bookingId',
                header: 'Reservation',
                size: 110,
                Cell: ({ cell }) => `#${cell.getValue<number>()}`,
            },
            {
                accessorKey: 'moduleName',
                header: 'Experience',
                size: 160,
            },
            {
                accessorKey: 'guestCompany',
                header: 'Guest Company',
                size: 160,
            },
            {
                accessorKey: 'startDate',
                header: 'From',
                size: 110,
                sortingFn: 'datetime',
                Cell: ({ cell }) =>
                    moment(cell.getValue<string>()).format('DD/MM/YYYY'),
            },
            {
                accessorKey: 'endDate',
                header: 'To',
                size: 110,
                sortingFn: 'datetime',
                Cell: ({ cell }) =>
                    moment(cell.getValue<string>()).format('DD/MM/YYYY'),
            },
        ],
        [theme],
    );

    const rows = attendees ?? [];

    if (!rows.length) {
        return (
            <Typography sx={{ mt: 4 }} variant='body2'>
                No individual external guest attendees found for this selection.
            </Typography>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <GenericTable
                {...dashboardItemsTableDefaults}
                columns={columns}
                data={rows}
                tableName='External Guest Attendees Report'
                paginationItemLabel='attendees'
                getRowId={(row) => String(row.id)}
            />
        </Box>
    );
}
